/* eslint-disable react/forbid-prop-types,no-param-reassign */
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Box, Collapse, Grid, Typography, Select, MenuItem, FormControl } from "@mui/material";
import _ from "lodash";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";

import SecondaryButton from "./SecondaryButton";
import Form from "../form/Form";

const Filter = ({ filterSteps, filterSchema, items, setFilteredItems, shouldStartOpen, hideInternalButton }) => {
  const [hideFilters, setHideFilters] = useState(shouldStartOpen);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);

  const form = useForm({
    resolver: yupResolver(filterSchema),
    mode: "onChange",
  });

  useEffect(() => {
    const hasFilters = Array.from(searchParams.keys()).filter((k) => k !== "_gl").length > 0;
    if (!hasFilters && items.length > 0) {
      setFilteredItems(items);
    }
  }, [items, searchParams]);

  const flatten = (obj, prefix = '', res = {}) =>
    Object.entries(obj).reduce((r, [key, val]) => {
      const k = prefix !== '' ? `${prefix}.${key}` : `${prefix}${key}`
      if(typeof val === 'object'){
        flatten(val, `${k}`, r)
      } else if (![null, undefined, ''].includes(val)) {
        res[k] = val
      }
      return r
    }, res)

  const handleSubmit = async () => {
    await form.trigger();
    if (Object.keys(form.formState.errors).length === 0) {
      const filterObject = flatten(form.getValues());
      
      setSearchParams((oldSearch) => ({ ...oldSearch, ...filterObject }))
      if (Object.keys(filterObject).length !== 0) {
        setFilteredItems(
          items.filter((i) => {
            const filterBoolResult = Object.keys(filterObject)
              .filter((filterKey) => filterObject[filterKey] !== undefined)
              .map((filterKey) => {
                if (typeof _.get(filterObject, filterKey) === 'boolean') {
                  return _.get(i, filterKey) === _.get(filterObject, filterKey)
                }

                if (/\.[0-9]+/.test(filterKey)) {
                  // eslint-disable-next-line prefer-destructuring
                  const tempFilterKey = filterKey.split(".")[0];
                  filterObject[tempFilterKey] = filterObject?.tempFilterKey ? [...filterObject.tempFilterKey, filterObject[filterKey]] : [filterObject[filterKey]];
                  delete filterObject[filterKey];
                  filterKey = tempFilterKey;
                }

                // Find the searchable field name from all filter steps, not just the first one
                const searchableFieldName = filterSteps.flatMap(step => step.fields).find((innerI) => innerI?.searchableFieldName && innerI.name === filterKey)?.searchableFieldName;
                const objectFilterKey = _.get(i, searchableFieldName || filterKey);
                const actualFilterKey = searchableFieldName || filterKey;

                if (!objectFilterKey) {
                  return false;
                }
                
                // Handle date filtering - check if it's a date field by name pattern
                if (filterKey.includes("Date") || filterKey.includes("date")) {
                  // Convert string to Date if needed
                  let filterDate = _.get(filterObject, filterKey);
                  if (typeof filterDate === 'string' && filterDate) {
                    filterDate = new Date(filterDate);
                  }
                  
                  if (filterDate instanceof Date && !Number.isNaN(filterDate.getTime())) {
                    if (filterKey.includes("startDate") || filterKey.includes("StartDate")) {
                      return new Date(objectFilterKey) > filterDate;
                    }
                    if (filterKey.includes("endDate") || filterKey.includes("EndDate")) {
                      return new Date(objectFilterKey) < filterDate;
                    }
                  }
                }
                if (Array.isArray(objectFilterKey)) {
                  const filterValue = _.get(filterObject, filterKey);
                  // Handle case where filter value is a string but objectFilterKey is an array
                  if (typeof filterValue === 'string') {
                    // For languages array, check if any language object contains the filter string
                    if (actualFilterKey === 'profile.languages') {
                      return objectFilterKey.some(langObj => 
                        langObj && typeof langObj === 'object' && 
                        langObj.language && 
                        langObj.language.toLowerCase().includes(filterValue.toLowerCase())
                      );
                    }
                    // For work experience array, search across relevant fields
                    if (actualFilterKey === 'profile.workExperience') {
                      // If it's the general "Work Experience" text filter, search multiple fields
                      if (filterKey === 'profile.workExperience') {
                        return objectFilterKey.some(workObj => 
                          workObj && typeof workObj === 'object' && (
                            (workObj.jobTitle && workObj.jobTitle.toLowerCase().includes(filterValue.toLowerCase())) ||
                            (workObj.employer && workObj.employer.toLowerCase().includes(filterValue.toLowerCase())) ||
                            (workObj.description && workObj.description.toLowerCase().includes(filterValue.toLowerCase()))
                          )
                        );
                      }
                      // If it's a specific filter like "industry" or "workExperinceWT", search only that field
                      const specificField = filterKey; // "industry" or "workExperinceWT"
                      return objectFilterKey.some(workObj => 
                        workObj && typeof workObj === 'object' && 
                        workObj[specificField] && 
                        workObj[specificField].toLowerCase().includes(filterValue.toLowerCase())
                      );
                    }
                    // For education array, search across relevant fields
                    if (actualFilterKey === 'profile.education') {
                      // If it's the general "Education" text filter, search multiple fields
                      if (filterKey === 'profile.education') {
                        return objectFilterKey.some(eduObj => 
                          eduObj && typeof eduObj === 'object' && (
                            (eduObj.institutionName && eduObj.institutionName.toLowerCase().includes(filterValue.toLowerCase())) ||
                            (eduObj.degree && eduObj.degree.toLowerCase().includes(filterValue.toLowerCase())) ||
                            (eduObj.educationLevel && eduObj.educationLevel.toLowerCase().includes(filterValue.toLowerCase()))
                          )
                        );
                      }
                      // If it's a specific filter like "degree" or "educationLevel", search only that field
                      const specificField = filterKey; // "degree" or "educationLevel"
                      return objectFilterKey.some(eduObj => 
                        eduObj && typeof eduObj === 'object' && 
                        eduObj[specificField] && 
                        eduObj[specificField].toLowerCase().includes(filterValue.toLowerCase())
                      );
                    }
                    // For other arrays, check if any element includes the filter string
                    return objectFilterKey.some(element => 
                      element && element.toString().toLowerCase().includes(filterValue.toLowerCase())
                    );
                  }
                  // Handle case where filter value is an array
                  if (Array.isArray(filterValue)) {
                    return filterValue.every(element => objectFilterKey.includes(element));
                  }
                  // Fallback for other cases
                  return objectFilterKey.includes(filterValue);
                }

                return objectFilterKey.toLowerCase().includes(_.get(filterObject, filterKey).toLowerCase())
              });
              return filterBoolResult.every((isTrue) => isTrue);
            })
        );
      } else {
        setFilteredItems(items);
      }
    }
  };

  useEffect(() => {
    const queryParams = Object.fromEntries(searchParams.entries());
    const filteredParams = Object.keys(queryParams).filter((k) => k !== "_gl");
    
    if (filteredParams.length > 0) {
      filteredParams.forEach((k) => {
      form.setValue(k, queryParams[k]);
      });
      // Delay the handleSubmit to ensure form values are set
      setTimeout(() => handleSubmit(), 100);
    }
  }, []);

  useEffect(() => {
    const subscription = form.watch(() => setTimeout(() => handleSubmit(), 600));
    return () => subscription.unsubscribe();
  }, [items, form.watch]);

  const toggleFilters = () => setHideFilters((val) => !val);

  // Expose toggle function to parent if hideInternalButton is true
  useEffect(() => {
    if (hideInternalButton && window.toggleFilters) {
      window.toggleFilters = toggleFilters;
    }
  }, [hideInternalButton]);

  return (
    <Box sx={{ marginBottom: '20px' }}>
      {!hideInternalButton && (
        <Box>
          <Box textAlign="right" marginBottom="20px">
            <SecondaryButton onClick={toggleFilters}>
              <span>{hideFilters ? "Hide Filters" : "Show Filters"}</span>
            </SecondaryButton>
          </Box>
        </Box>
      )}
      <Collapse orientation="vertical" in={hideFilters}>
        <Form
          steps={filterSteps}
          form={form}
          onSubmit={handleSubmit}
          startPage={currentStep}
          FormButtons={
            <Grid container sx={{ width: "40%", margin: "auto", display: "flex", justifyContent: "center" }}>
              <Grid item xs={12} sm={12} md={6} lg={6} sx={{ display: "flex", justifyContent: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: "15px", fontWeight: "400", cursor: "pointer", textAlign: "center", width: "100%" }}
                  onClick={async () => {
                    await form.reset();
                    setFilteredItems(items);
                  }}
                >
                  Reset all filters
                </Typography>
              </Grid>
              {/* <Grid item xs={12} sm={12} md={6} lg={6} sx={{ marginLeft: "auto" }}>
                <PrimaryButton type="submit">Search</PrimaryButton>
              </Grid> */}
            </Grid>
          }
          options={{
            showTitle: true,
            sameLine: true,
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '10px', mt: 2, mx: { xs: 2, md: 0 } }}>
          {/* Desktop view - individual buttons */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: '10px' }}>
            {filterSteps.map((step, index) => (
              <SecondaryButton
                key={step.title}
                onClick={() => setCurrentStep(index)}
                sx={{
                  backgroundColor: currentStep === index ? '#C02026' : 'transparent',
                  color: currentStep === index ? 'white' : 'inherit',
                }}
              >
                {step.title}
              </SecondaryButton>
            ))}
          </Box>
          
          {/* Mobile view - dropdown */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, minWidth: '200px' }}>
            <FormControl fullWidth size="small">
              <Select
                value={currentStep}
                onChange={(e) => setCurrentStep(e.target.value)}
                displayEmpty
                sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#C02026',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#C02026',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#C02026',
                  },
                }}
              >
                {filterSteps.map((step, index) => (
                  <MenuItem key={step.title} value={index}>
                    {step.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

Filter.propTypes = {
  items: PropTypes.array,
  setFilteredItems: PropTypes.func.isRequired,
  filterSchema: PropTypes.object.isRequired,
  filterSteps: PropTypes.array.isRequired,
  shouldStartOpen: PropTypes.bool,
  hideInternalButton: PropTypes.bool,
};

Filter.defaultProps = {
  items: [],
  shouldStartOpen: false,
  hideInternalButton: false,
};

export default Filter;
